/**
 * Rekordbox XML collection parser.
 *
 * Pioneer's Rekordbox app exports the whole library as an XML file:
 *   File → Export Collection in xml format
 *
 * The XML shape (simplified — Rekordbox 6.x + 7.x are backwards-compat):
 *   <DJ_PLAYLISTS>
 *     <COLLECTION Entries="1234">
 *       <TRACK TrackID="12" Name="Losing It" Artist="Fisher" AverageBpm="127.50"
 *              Tonality="8A" TotalTime="245" Genre="Tech House"
 *              Location="file://localhost/Users/dj/Music/Fisher/Losing%20It.mp3">
 *         <TEMPO Inizio="0.253" Bpm="127.50" Metro="4/4" Battito="1"/>
 *         <POSITION_MARK Name="Intro" Type="0" Start="0.253" Num="0"/>
 *         <POSITION_MARK Name="Drop"  Type="0" Start="61.5"  Num="1"/>
 *       </TRACK>
 *       ... more TRACK entries
 *     </COLLECTION>
 *     <PLAYLISTS>
 *       <NODE Type="0" Name="ROOT" Count="4">
 *         <NODE Type="1" Name="Peak Time" KeyType="0" Entries="12">
 *           <TRACK Key="12"/>
 *           <TRACK Key="45"/>
 *           ...
 *         </NODE>
 *       </NODE>
 *     </PLAYLISTS>
 *   </DJ_PLAYLISTS>
 *
 * We flatten this into the same shape as our Serato parser so the same
 * localStorage store + UI works for both.
 */

const decodeLocation = (loc) => {
  if (!loc) return null;
  try {
    // file://localhost/Users/... → /Users/...
    const noProto = loc.replace(/^file:\/\/localhost/i, "").replace(/^file:\/\//i, "");
    return decodeURIComponent(noProto);
  } catch { return loc; }
};

const basename = (path) => (path || "").split(/[/\\]/).pop() || "";

/**
 * Parse a Rekordbox XML text blob.
 * @param {string} text
 * @returns {{ crates: Array<{ name: string, tracks: Array }> }}
 *   If the XML has playlists, each playlist becomes a crate. If it only has
 *   COLLECTION, we return one crate named "Rekordbox Collection" with everything.
 */
export function parseRekordboxXML(text) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("Malformed XML — this doesn't look like a Rekordbox export.");
  }

  // Build TrackID → track-object map from COLLECTION first.
  const collectionById = new Map();
  const allTracks = [];
  for (const el of doc.querySelectorAll("COLLECTION > TRACK")) {
    const location = decodeLocation(el.getAttribute("Location"));
    const t = {
      artist: el.getAttribute("Artist") || "",
      title:  el.getAttribute("Name") || basename(location).replace(/\.[^.]+$/, "") || "Untitled",
      duration_seconds: parseFloat(el.getAttribute("TotalTime")) || null,
      bpm: parseFloat(el.getAttribute("AverageBpm")) || null,
      key: el.getAttribute("Tonality") || null,      // Camelot when KeyType=1 in Rekordbox settings
      genre: el.getAttribute("Genre") || null,
      file_path: location,
      filename: basename(location),
      // Hot-cue positions (seconds) — Rekordbox stores up to 8
      hot_cues: Array.from(el.querySelectorAll("POSITION_MARK"))
        .filter(pm => pm.getAttribute("Type") === "0")   // Type 0 = memory cue
        .slice(0, 8)
        .map(pm => ({
          name: pm.getAttribute("Name") || "",
          start_seconds: parseFloat(pm.getAttribute("Start")) || 0,
          num: parseInt(pm.getAttribute("Num") || "0", 10),
        })),
    };
    const id = el.getAttribute("TrackID");
    if (id) collectionById.set(id, t);
    allTracks.push(t);
  }

  // Walk PLAYLISTS/NODE structure. Playlists are leaf NODEs (Type="1").
  const crates = [];
  const walk = (node, ancestry = []) => {
    const type = node.getAttribute("Type");
    const name = node.getAttribute("Name") || "";
    if (type === "1") {
      // Playlist leaf
      const tracks = [];
      for (const tref of node.querySelectorAll(":scope > TRACK")) {
        const key = tref.getAttribute("Key");
        const t = collectionById.get(key);
        if (t) tracks.push(t);
      }
      if (tracks.length) {
        const fullName = ancestry.filter(a => a && a !== "ROOT").concat(name).join(" / ") || name;
        crates.push({ name: fullName, tracks });
      }
    } else {
      // Folder node — recurse
      for (const child of node.querySelectorAll(":scope > NODE")) {
        walk(child, ancestry.concat(name));
      }
    }
  };
  const root = doc.querySelector("PLAYLISTS > NODE");
  if (root) walk(root);

  // Fallback: no playlists found → surface the whole collection as one crate.
  if (!crates.length && allTracks.length) {
    crates.push({ name: "Rekordbox Collection", tracks: allTracks });
  }

  return { crates };
}
